import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ArrowLeft, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ForgotPassword = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes('@')) {
      toast.error('Ongeldig e-mailadres');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      // Backend always returns success — but handle defensively
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a2a2a] via-[#0d3d3d] to-[#1a5656] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center text-white/70 hover:text-white text-sm mb-4 gap-1">
          <ArrowLeft className="w-4 h-4" /> Terug naar inloggen
        </Link>
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-white/20" data-testid="forgot-password-card">
          <div className="flex items-center gap-3 mb-3">
            <Mail className="w-7 h-7 text-[#3da5a5]" />
            <h1 className="text-2xl font-bold text-white">Wachtwoord vergeten</h1>
          </div>
          {sent ? (
            <div className="text-center space-y-3 py-4">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#3da5a5]/20 flex items-center justify-center">
                <Mail className="w-8 h-8 text-[#3da5a5]" />
              </div>
              <h2 className="text-xl font-semibold text-white">E-mail verzonden</h2>
              <p className="text-white/70 text-sm">
                Als <strong className="text-white">{email}</strong> bekend is in ons systeem, ontvangt u zo een link om uw wachtwoord opnieuw in te stellen.
              </p>
              <p className="text-white/50 text-xs">
                De link is <strong>1 uur</strong> geldig. Niets ontvangen? Controleer ook uw spam-map.
              </p>
              <Link to="/login" className="inline-block mt-2">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10">Terug naar inloggen</Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <p className="text-white/70 text-sm">
                Vul uw e-mailadres in. Wij sturen u een veilige link om uw wachtwoord opnieuw in te stellen.
              </p>
              <div>
                <Label className="text-white mb-2 block">E-mail *</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="uw@email.nl"
                  className="bg-white/10 border-white/30 text-white"
                  data-testid="forgot-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !email}
                className="w-full bg-[#3da5a5] hover:bg-[#2d8585] py-6 text-lg font-semibold"
                data-testid="forgot-submit-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verstuur reset-link'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
